import React, { useState } from 'react';
import 'react-dual-listbox/lib/react-dual-listbox.css';

import { ButtonsContainer, SelectContainer, ViewContainer } from './styles';

import api from '~/services/api';
import { toast } from 'react-toastify';

import { useImpressao } from '~/components/Impressao/ImpressaoContext';
import CreatableSelect from 'react-select/creatable';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import { Campo, FormView, Option } from '~/components/Impressao/types';
import { CustomButtonNovo } from '~/components/Buttons/CustomButtonNovo';

const MySwal = withReactContent(Swal);

/**
 * ViewComponent
 * @description
 * Gerencia criação/seleção/remoção de “Visualizações” (impressões) e sincroniza o estado global.
 * Mantém toda a funcionalidade do Atlaskit usando react-select/creatable:
 * - Criar opção via digitação (onCreateOption)
 * - Mensagem customizada de criação (formatCreateLabel)
 * - Mensagem de lista vazia (noOptionsMessage)
 * - Controle de input (inputValue/onInputChange) com UPPERCASE e limite de 50 chars
 * - Acessibilidade via label + inputId
 */
export const ViewComponent: React.FC = () => {
  const [inputvalue, setinputvalue] = useState('');

  const {
    changeSelected,
    changeSelectedView,
    isChangeView,
    handleIsChangeView,
    handleHasSelectedView,
    handleChangeFormView,
    isNewView,
    handleIsNewView,
    handleSaveView,
    selectedView,
    handleSelectedView,
    selectedOption,
    handleSelectedOption,
    views,
    optionsSelect,
    handleOptionsSelect,
    getViews,
    optionsAvailable,
    setOptionsAvailable,
    selected,
    optionsAvailableDefault,
    setBtnCount,
    setBtnSomar,
  } = useImpressao();

  /** Cria uma nova visualização a partir do texto digitado. */
  const onCreateView = (inputValue: string) => {
    const valueToUpper = inputValue.toUpperCase();
    handleChangeFormView({ des_impressao: valueToUpper });

    const option: Option = {
      label: valueToUpper,
      value: valueToUpper,
    };

    handleOptionsSelect([...optionsSelect, option]);
    handleSelectedOption(option);
    handleHasSelectedView(true);
    handleIsNewView(true);
    handleIsChangeView(true);
  };

  /** Fluxo de exclusão (nova não salva vs já persistida). */
  const onDeleteView = async () => {
    try {
      MySwal.fire({
        title: '',
        text: 'Deseja remover a visualização?',
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#0065FF',
        cancelButtonColor: '#DE350B',
        icon: 'warning',
      }).then(async (result) => {
        if (!result.isConfirmed) return;

        if (isNewView) {
          const options = optionsSelect.filter(
            (option) => selectedOption?.value !== option.value,
          );
          const optionConcat = optionsAvailable.concat(selected);

          optionConcat.sort((a, b) =>
            a.des_campo < b.des_campo ? -1 : a.des_campo > b.des_campo ? 1 : 0,
          );

          setOptionsAvailable(optionConcat);
          handleOptionsSelect(options);
          handleSelectedOption(null);
          changeSelected([]);
          handleIsChangeView(false);
          return;
        }

        const res = await api.delete(
          `/impressao/view/${selectedView?.cod_impressao}`,
        );
        const { success, message } = res.data;
        if (!success) throw new Error(message);

        const options = optionsAvailable.concat(selected);
        options.sort((a, b) =>
          a.des_campo < b.des_campo ? -1 : a.des_campo > b.des_campo ? 1 : 0,
        );

        setOptionsAvailable(options);
        handleSelectedOption(null);
        getViews();
        changeSelected([]);
        handleIsChangeView(false);
        handleHasSelectedView(false);
        toast.success(message);
      });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  /**
   * Exibe confirmação quando há alterações pendentes.
   * @param callback Recebe true (salvar antes) ou false (seguir sem salvar).
   */
  const confirmChange = (callback: (value: boolean) => void) => {
    MySwal.fire({
      title: '',
      text: 'Há alterações pendentes. Deseja salvá-las antes de continuar?',
      showCancelButton: true,
      confirmButtonText: 'Salvar Alterações',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0065FF',
      cancelButtonColor: '#DE350B',
      allowOutsideClick: false,
      icon: 'warning',
    }).then(async (result) => {
      if (result.isDismissed) return callback(false);
      return callback(true);
    });
  };

  /**
   * Troca de visualização selecionada.
   * Mantém toda a lógica existente, apenas adaptando o tipo do value do react-select.
   */
  const handleChangeView = async (value: Option | null) => {
    if (value) {
      if (isChangeView) {
        await confirmChange(async (confirmed) => {
          if (confirmed) await handleSaveView();
        });
      }

      handleIsNewView(false);
      handleHasSelectedView(true);
      handleSelectedOption(value);

      const selectedsFromView = views.find(
        (view) => view.cod_impressao === value.value,
      );
      if (selectedsFromView) {
        handleSelectedView(selectedsFromView);

        const newSelecteds = selectedsFromView.campos.map((item: Campo) => {
          let desCampo = '';

          const optionsAvailableFind = optionsAvailableDefault.find(
            (option) => Number(option.cod_campo) === item.cod_campo,
          );
          if (optionsAvailableFind) desCampo = optionsAvailableFind.des_campo;

          const contarObj = selectedsFromView.campos
            .filter((campo) => campo.flg_contar)
            .reduce((acc, campo) => {
              acc[campo.cod_campo.toString()] = true;
              return acc;
            }, {} as { [key: string]: boolean });

          const somarObj = selectedsFromView.campos
            .filter((campo) => campo.flg_somar)
            .reduce((acc, campo) => {
              acc[campo.cod_campo.toString()] = true;
              return acc;
            }, {} as { [key: string]: boolean });

          setBtnCount(contarObj);
          setBtnSomar(somarObj);

          return {
            order: item.num_ordem,
            cod_campo: item.cod_campo,
            des_campo: desCampo,
          };
        });

        const filteredOptions = optionsAvailableDefault.filter(
          (option) =>
            !newSelecteds.some(
              (selected2: any) =>
                Number(selected2.cod_campo) === Number(option.cod_campo),
            ),
        );

        setOptionsAvailable(filteredOptions);
        return changeSelectedView(newSelecteds);
      }
      return;
    }

    handleHasSelectedView(false);
    return changeSelectedView([]);
  };

  /** Limpa a visualização, respeitando o fluxo de confirmação quando há alterações. */
  const onClearView = async () => {
    if (isChangeView) {
      await confirmChange(async (confirm) => {
        if (confirm) {
          await handleSaveView();
        } else if (isNewView) {
          const options = optionsSelect.filter(
            (option) => selectedOption?.value !== option.value,
          );
          handleOptionsSelect(options);
        }
        setOptionsAvailable(optionsAvailableDefault);
        handleSelectedOption(null);
        changeSelected([]);
        handleIsChangeView(false);
        handleHasSelectedView(false);
      });
      return;
    }

    if (isNewView) {
      const options = optionsSelect.filter(
        (option) => selectedOption?.value !== option.value,
      );
      handleOptionsSelect(options);
    }

    setOptionsAvailable(optionsAvailableDefault);
    handleHasSelectedView(false);
    handleSelectedOption(null);
    changeSelected([]);
    handleIsChangeView(false);
    handleChangeFormView({} as FormView);
  };

  return (
    <ViewContainer>
      <SelectContainer>
        <label htmlFor="view-select" className="mb-1">
          Visualização
        </label>

        <CreatableSelect<Option, false>
          // Acessibilidade: conecta o <label> ao input do select
          inputId="view-select"
          className="view-select"
          classNamePrefix="view-select"
          placeholder="Digite um nome para Cadastro ou Selecione uma Visualização"
          // Mensagem para criar
          formatCreateLabel={(userInput: string) =>
            `TOQUE AQUI para criar a visualização '${userInput.toUpperCase()}'`
          }
          // Mensagem quando não há opções
          noOptionsMessage={() => 'Nenhum Registro Encontrado'}
          // Controle do texto digitado (UPPERCASE + limite 50)
          inputValue={inputvalue}
          onInputChange={(newValue) => {
            const next = newValue.toUpperCase().slice(0, 50);
            setinputvalue(next);
            return next;
          }}
          // Dados
          value={selectedOption as Option | null}
          options={optionsSelect}
          // Seleção/criação
          onChange={(opt) => handleChangeView((opt ?? null) as Option | null)}
          onCreateOption={onCreateView}
          // Comportamento Enter igual ao anterior
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' && !selectedOption) {
              ev.preventDefault();
              ev.stopPropagation();
              setTimeout(() => {
                handleIsChangeView(false);
              }, 200);
            }
          }}
          // Estilos básicos para ocupar largura total (opcional)
          styles={{
            container: (base) => ({ ...base, width: '100%' }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
          menuPortalTarget={
            typeof window !== 'undefined' ? document.body : null
          }
        />
      </SelectContainer>

      <ButtonsContainer>
        <CustomButtonNovo
          onClick={handleSaveView}
          disabled={!isChangeView}
          variant="confirm"
          label="Salvar"
        />

        <CustomButtonNovo
          onClick={onClearView}
          disabled={!selectedOption}
          variant="clear"
          label="Limpar"
        />

        <CustomButtonNovo
          onClick={onDeleteView}
          disabled={!selectedOption}
          variant="error"
          label="Excluir"
        />
      </ButtonsContainer>
    </ViewContainer>
  );
};
